/**
 * useCapacityTaskDetail — OutpostForge frontend (CES standard)
 * Detail of one capacity task with encapsulated polling; while the task is
 * WAITING_FOR_EVACUATION it also polls the blocking instances panel data.
 */

import { useEffect, useState } from 'react';
import {
  capacityTaskRepository,
  type CapacityTaskRepository,
} from '../repositories/CapacityTaskRepository';
import { describeApiError } from '../services/apiErrors';
import type { BlockingInstance, CapacityTaskResponse } from '../types/outposts';
import { TASK_POLL_MS } from './useCapacityTasks';

export function useCapacityTaskDetail(
  outpostId: string,
  taskId: string,
  repository: CapacityTaskRepository = capacityTaskRepository,
) {
  const [detail, setDetail] = useState<CapacityTaskResponse | null>(null);
  const [blocking, setBlocking] = useState<BlockingInstance[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      repository
        .get(outpostId, taskId)
        .then((task) => {
          if (cancelled) return;
          setDetail(task);
          setError(null);
          if (task.status === 'WAITING_FOR_EVACUATION') {
            repository
              .getBlockingInstances(outpostId, taskId)
              .then((instances) => {
                if (!cancelled) setBlocking(instances);
              })
              .catch(() => {});
          } else {
            setBlocking([]);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(describeApiError(err));
        });
    };
    load();
    const interval = setInterval(load, TASK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [outpostId, taskId, repository]);

  return { detail, blocking, error };
}
